import React, { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { isSamePath } from './usePermission';

/** Acciones efectivas del usuario en una vista (el back ya aplicó: sin configurar = permitido). */
export type TViewPermission = {
  smId: string;
  smCode: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

export type TPermissionAction = 'view' | 'create' | 'edit' | 'delete';

/** Operación del registro central del back (services/permissions/registry.js). */
export type TPermissionOperation = {
  key: string;
  paths: string[];
  action: TPermissionAction;
};

type TPermissionsContext = {
  viewPermissions: TViewPermission[];
  operations: TPermissionOperation[];
  /** Vista de la página actual: el path que declara la ruta en ProtectedRoute. */
  currentPath: string;
};

const ACTION_FIELDS: Record<Exclude<TPermissionAction, 'view'>, keyof TViewPermission> = {
  create: 'canCreate',
  edit: 'canEdit',
  delete: 'canDelete',
};

const ACTION_LABELS: Record<TPermissionAction, string> = {
  view: 'ver',
  create: 'crear',
  edit: 'editar',
  delete: 'eliminar',
};

/** Mensaje único cuando falta un permiso (tooltips de botones deshabilitados). */
export const getDeniedMessage = (action: TPermissionAction) =>
  `No tienes permiso para ${ACTION_LABELS[action]} en esta sección. Si lo necesitas, solicítalo a un administrador.`;

// Sin proveedor (fuera del layout) no hay información de permisos: todo se comporta como antes.
const PermissionsContext = createContext<TPermissionsContext | null>(null);

export const PermissionsProvider: React.FC<TPermissionsContext & { children: ReactNode }> = ({
  viewPermissions,
  operations,
  currentPath,
  children,
}) => {
  const value = useMemo(
    () => ({ viewPermissions, operations, currentPath }),
    [viewPermissions, operations, currentPath]
  );
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

/** Busca la vista por smCode exacto, la misma regla que usa el acceso a la página (getPermission). */
const findView = (viewPermissions: TViewPermission[], path: string) =>
  viewPermissions.find((view) => isSamePath(view.smCode, path));

/**
 * @example
 */
export const useActionPermissions = () => {
  const context = useContext(PermissionsContext);

  const can = (action: TPermissionAction, path?: string | string[]) => {
    if (!context) return true;
    const { viewPermissions, currentPath } = context;
    const targets = path ? ([] as string[]).concat(path) : [currentPath];
    const views = targets
      .map((target) => findView(viewPermissions, target))
      .filter((view): view is TViewPermission => !!view);
    if (!views.length) return false;
    if (action === 'view') return true;
    return views.every((view) => Boolean(view[ACTION_FIELDS[action]]));
  };

  const getOperation = (key: string) => context?.operations.find((item) => item.key === key);

  const canDo = (key: string) => {
    if (!context) return true;
    const operation = getOperation(key);
    if (!operation) {
      // Operación no registrada en el back: no se bloquea, pero se avisa para corregirlo.
      // eslint-disable-next-line no-console
      console.warn(`[Permisos] La operación "${key}" no existe en el registro del back.`);
      return true;
    }
    return can(operation.action, operation.paths);
  };

  return { can, canDo, getOperation };
};

type TCanProps = {
  operation?: string;
  action?: TPermissionAction;
  path?: string | string[];
  mode?: 'hide' | 'disable';
  children: ReactElement;
};

/**
 * Sin permiso, el botón se muestra deshabilitado con el mensaje (o se oculta con mode="hide").
 * Si el botón está dentro de un <Link>, se quita el enlace para que no navegue.
 * @example <Can operation="seller.create"><Link to="…"><Button>Crear comercial</Button></Link></Can>
 * @example <Can action="delete" mode="hide"><IconButton>…</IconButton></Can>
 */
export const Can: React.FC<TCanProps> = ({
  operation,
  action = 'view',
  path,
  mode = 'disable',
  children,
}) => {
  const { can, canDo, getOperation } = useActionPermissions();

  if (operation ? canDo(operation) : can(action, path)) return children;
  if (mode === 'hide') return null;

  const deniedAction = (operation && getOperation(operation)?.action) || action;
  const button =
    children.type === Link && React.isValidElement(children.props.children)
      ? (children.props.children as ReactElement)
      : children;
  // en los íconos (editar, eliminar) el color propio del ícono taparía el estado deshabilitado;
  // un ícono suelto con onClick no tiene "disabled": se le quita el clic
  const isIcon = (button.type as { muiName?: string })?.muiName === 'SvgIcon';
  let disabledProps: Record<string, unknown> = { disabled: true };
  if (button.type === IconButton) {
    disabledProps = {
      disabled: true,
      sx: { ...button.props.sx, '& .MuiSvgIcon-root': { color: 'action.disabled' } },
    };
  } else if (isIcon) {
    disabledProps = {
      onClick: undefined,
      color: 'disabled',
      sx: { ...button.props.sx, cursor: 'not-allowed' },
    };
  }
  return (
    <Tooltip title={getDeniedMessage(deniedAction)}>
      <span style={{ display: 'inline-flex' }}>{React.cloneElement(button, disabledProps)}</span>
    </Tooltip>
  );
};

export const PermissionTooltip: React.FC<{
  allowed: boolean;
  action: TPermissionAction;
  children: ReactElement;
}> = ({ allowed, action, children }) => {
  if (allowed) return children;

  return (
    <Tooltip title={getDeniedMessage(action)}>
      <span style={{ display: 'inline-flex' }}>{children}</span>
    </Tooltip>
  );
};
